"use client";

import { useState } from "react";
import Link from "next/link";
import LightbulbIcon from "@/app/assets/images/icons/lightbulb.svg";
import InfoTooltip from "@/app/components/InfoTooltip";
import { TIER_DEFINITIONS } from "@/lib/config/tiers/definitions";
import type { UserTier } from "@/lib/config/tiers/types";

export type PaymentFrequency = "annual" | "monthly";

interface TiersPricingTableProps {
  currentTier?: UserTier;
  showButtons?: boolean;
  buttonTitle?: string;
  currentTierButtonTitle?: string;
  includeFeatureFlags?: boolean;
  paymentFrequency: PaymentFrequency;
  onTierButtonClicked?: (_tierId: UserTier) => void;
}

export function TiersPricingTable({
  currentTier,
  showButtons = true,
  buttonTitle = "Select Plan",
  currentTierButtonTitle = "Current Plan",
  includeFeatureFlags = false,
  paymentFrequency,
  onTierButtonClicked,
}: TiersPricingTableProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const QUOTA_INFO: Record<string, string> = {
    "AI Credits/month":
      "AI credits power chatbot replies, reports and other AI-driven features. Your credits are not used up for the processing of incoming messages into your groups.",
    "Stored Chat Messages":
      "Chat messages from all groups are safely stored to secure your community's knowledge",
    "Incoming Messages / month":
      "Messages sent by your community members in connected chat groups. Additional messages are charged at a per-message rate based on your tier.",
    "Automations / month":
      "Automations are optional events you wire up, such as being notified about particular new threads or topics.",
  };

  // Get tiers that should be in main pricing table, sorted by sortOrder
  const pricingTiers = Object.values(TIER_DEFINITIONS)
    .filter(tier => tier.isInMainPricingTable && tier.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Check if the user's current tier is outside the main pricing table
  const currentTierDefinition = currentTier ? TIER_DEFINITIONS[currentTier] : undefined;
  const isCustomTier = currentTier && currentTierDefinition && !pricingTiers.some(t => t.id === currentTier);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const formatPrice = (cents: number): string => {
    const dollars = cents / 100;
    return `£${dollars.toFixed(0)}`;
  };

  const formatQuota = (value: number): string => {
    if (value === -1) return "Unlimited";
    return value.toLocaleString();
  };

  if (isCustomTier && currentTierDefinition) {
    return (
      <div className="flex justify-center w-full">
        <div className="card bg-base-200 shadow-xl max-w-lg w-full">
          <div className="card-body items-center text-center">
            <LightbulbIcon className="w-16 h-16 text-base-content/60" />
            <h2 className="card-title text-2xl mb-4">
              Your {currentTierDefinition.name} Plan
            </h2>
            <p className="text-base-content/80">
              You are currently on an enterprise or custom plan that cannot be
              upgraded or changed automatically. To discuss your individual
              quotas or requirements, please{" "}
              <Link
                href="mailto:webcontact@threadhound.ai"
                className="link link-primary"
              >
                contact the team directly
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 w-full justify-center items-stretch">
      {pricingTiers.map((tier) => {
        const isCurrentTier = currentTier === tier.id;
        const activePrice =
          paymentFrequency === "annual"
            ? tier.pricing.annualPrice
            : tier.pricing.monthlyPrice;
        const isContactUs = activePrice === -1;

        // Key features from chatbot feature flags
        const chatbotFeatures = [
          { name: "Knowledge Replies", enabled: tier.featureFlags.chatbot.moderationEnabled },
          { name: "Proactive Mode", enabled: tier.featureFlags.chatbot.proactiveModeEnabled },
          { name: "Reactions", enabled: tier.featureFlags.chatbot.reactionsEnabled },
          { name: "Welcome Messages", enabled: tier.featureFlags.chatbot.welcomeNewMembersEnabled },
        ];

        // Key quotas (always visible)
        const keyQuotas = [
          { name: "Stored Chat Messages", value: tier.databaseQuotas.chatMessages },
          { name: "Chat Groups", value: tier.databaseQuotas.connectors },
          { name: "Incoming Messages / month", value: tier.monthlyQuotas.messagesPerMonth },
          { name: "AI Credits/month", value: tier.monthlyQuotas.aiCredits },
        ];

        // Additional quotas (shown when expanded)
        const additionalQuotas = [
          { name: "Additional Team Members", value: tier.databaseQuotas.additionalUsers },
          { name: "Automations / month", value: tier.monthlyQuotas.automationsPerMonth },
        ];

        return (
          <div
            key={tier.id}
            className={`card bg-base-200 shadow-xl flex-1 max-w-sm ${isCurrentTier ? "ring-2 ring-primary" : ""
              }`}
          >
            <div className="card-body">
              {/* Tier name and current badge */}
              <div className="flex items-center justify-between">
                <h2 className="card-title text-2xl">{tier.name}</h2>
                {isCurrentTier && (
                  <span className="badge badge-primary">Current</span>
                )}
              </div>

              {/* Price - large number is always the per-month figure */}
              <div className="my-4">
                {isContactUs ? (
                  <>
                    <div className="text-4xl font-bold">Contact us</div>
                    <div className="text-sm text-base-content/70">
                      for a price based on your community&apos;s needs
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-4xl font-bold">
                      {paymentFrequency === "annual"
                        ? formatPrice(Math.round(tier.pricing.annualPrice / 12))
                        : formatPrice(tier.pricing.monthlyPrice)}
                      <span className="ml-2 text-2xl">per month</span>
                    </div>
                    {paymentFrequency === "annual" && tier.pricing.annualPrice > 0 && (
                      <div className="text-sm text-base-content/70">
                        based on annual payment of {formatPrice(tier.pricing.annualPrice)}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Marketing description */}
              <p className="text-base text-base-content/80 mb-4">
                {tier.marketingDescription}
              </p>

              <div className="divider my-2"></div>

              {/* Key features */}
              {includeFeatureFlags && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-sm uppercase tracking-wide">
                    Chatbot Features
                  </h3>
                  <ul className="space-y-2">
                    {chatbotFeatures.map((feature) => (
                      <li key={feature.name} className="flex items-center gap-2 text-sm">
                        {feature.enabled ? (
                          <svg className="w-5 h-5 text-success shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-base-content/30 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <span className={feature.enabled ? "" : "text-base-content/50"}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key quotas */}
              <div className="mb-4">
                <ul className="space-y-2">
                  {keyQuotas.map((quota) => {
                    const info = QUOTA_INFO[quota.name];
                    return (
                      <li key={quota.name} className="flex justify-between text-sm">
                        <span className="flex items-center gap-1">
                          {quota.name}
                          {info && <InfoTooltip info={info} ariaLabel={`More information about ${quota.name}`} />}
                        </span>
                        <span className="font-semibold">{formatQuota(quota.value)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Expandable section */}
              {additionalQuotas.length > 0 && (
                <>
                  {isExpanded && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-2 text-sm uppercase tracking-wide">
                        Additional Details
                      </h3>
                      <ul className="space-y-2">
                        {additionalQuotas.map((quota) => {
                          const info = QUOTA_INFO[quota.name];
                          return (
                            <li key={quota.name} className="flex justify-between text-sm">
                              <span className="flex items-center gap-1">
                                {quota.name}
                                {info && <InfoTooltip info={info} ariaLabel={`More information about ${quota.name}`} />}
                              </span>
                              <span className="font-semibold">
                                {quota.name === "Additional Team Members" && quota.value === 0
                                  ? "None"
                                  : formatQuota(quota.value)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  <button
                    className="btn btn-ghost btn-sm mb-4"
                    onClick={toggleExpanded}
                  >
                    {isExpanded ? "Show Less" : "Show More"}
                  </button>
                </>
              )}

              {/* Action button */}
              {showButtons && (
                <div className="card-actions justify-stretch mt-auto">
                  {isContactUs ? (
                    <Link href="/contact" className="btn btn-primary w-full">
                      Contact us
                    </Link>
                  ) : (
                    <button
                      className={`btn w-full ${isCurrentTier ? "btn-outline" : "btn-primary"
                        }`}
                      onClick={() => onTierButtonClicked?.(tier.id)}
                    >
                      {isCurrentTier ? currentTierButtonTitle : buttonTitle}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
